import { z } from "zod";

/**
 * The memory panel's wire shape (GAPS §3, "a memory browser"): what an agent
 * believes, fact by fact, with where each came from; the facts it was told
 * to forget; and the last background tidy, so it can be undone.
 */

/** Who last changed a fact: the bot's own tool, the extractor after an exchange, an episode, the background tidy, or the user. */
export const FactOriginSchema = z.enum(["bot", "extractor", "episode", "tidy", "user"]);
export type FactOrigin = z.infer<typeof FactOriginSchema>;

/** Where a fact came from, when the memory recorded it (facts from before provenance have none). */
export const FactSourceSchema = z
  .object({
    by: FactOriginSchema,
    /** When the change was made, ms since the epoch. */
    at: z.number(),
    /** The user's message it came out of, clipped. */
    said: z.string().optional(),
  })
  .strict();
export type FactSource = z.infer<typeof FactSourceSchema>;

export const MemoryFactSchema = z
  .object({
    id: z.string().min(1),
    content: z.string(),
    /** The day it was learned, ms since the epoch. */
    createdAt: z.number(),
    kind: z.enum(["profile", "log"]),
    /** The agent's own memory, or the user memory shared by every agent. */
    scope: z.enum(["agent", "user"]),
    /** Who learned it, in words: "your own memory", "shared about the user, by Ada". */
    via: z.string(),
    /** Whether this agent may forget it: its own facts, and the shared ones it recorded itself. */
    own: z.boolean(),
    /** For a fact: how it was learned. For a forgotten one: how it was forgotten. */
    source: FactSourceSchema.optional(),
    /** For a fact: the older facts it replaced, which are now forgotten. */
    replaced: z.array(z.string()).optional(),
    /** For a forgotten fact: the fact that replaced it. */
    replacedBy: z.string().optional(),
  })
  .strict();
export type MemoryFact = z.infer<typeof MemoryFactSchema>;

export const MemoryTidySchema = z
  .object({
    at: z.number(),
    removed: z.number().int().nonnegative(),
    added: z.number().int().nonnegative(),
    /** One line on what the tidy did, for the panel. */
    summary: z.string(),
  })
  .strict();
export type MemoryTidy = z.infer<typeof MemoryTidySchema>;

export const MemoryListResponseSchema = z
  .object({
    facts: z.array(MemoryFactSchema),
    forgotten: z.array(MemoryFactSchema),
    directory: z.string(),
    /** The last background tidy still on the undo stack, if any. */
    lastTidy: MemoryTidySchema.nullable(),
    /** The search-only conversation archive: how many stretches it holds and since when; the bot reads it only through recall_memory. */
    archive: z.object({ conversations: z.number().int().nonnegative(), since: z.number().nullable() }).strict().optional(),
  })
  .strict();
export type MemoryListResponse = z.infer<typeof MemoryListResponseSchema>;
